<template>
  <div class="dashboard-panel print-break-inside-avoid">
    <h3 class="section-title mb-4">
      <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Capital Raises Chart
    </h3>
    
    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-500">Loading chart...</p>
    </div>
    
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4">
      <p>{{ error }}</p>
    </div>
    
    <div v-else>
      <div ref="chartContainer" style="height: 300px;"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  raises: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null }
})

const chartContainer = ref(null)
let chart = null

/** Solid colours per development (up to 8 devs) */
const DEV_COLOURS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'
]

/** Lighter/transparent version of a hex colour for "to raise" bars */
const lightenHex = (hex, opacity = 0.35) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${opacity})`
}

const formatMonthLabel = (monthStr) => {
  const [year, monthNum] = monthStr.split('-')
  return new Date(year, monthNum - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const initChart = () => {
  if (!chartContainer.value) return
  if (chart) {
    chart.dispose()
    chart = null
  }
  chart = echarts.init(chartContainer.value)
  updateChart()
}

const updateChart = () => {
  if (!chart) return

  if (!props.raises.length) {
    chart.setOption({
      title: {
        text: 'No capital raises for selected development',
        left: 'center',
        top: 'middle',
        textStyle: { color: '#999', fontSize: 14 }
      },
      xAxis: { show: false },
      yAxis: { show: false },
      series: []
    })
    return
  }

  // Collect all months and developments
  const months = new Set()
  const devSet = new Set()

  props.raises.forEach(item => {
    months.add(item.month.substring(0, 7))
    devSet.add(item.development)
  })

  const sortedMonths = Array.from(months).sort()
  const developments = Array.from(devSet).sort()

  // Build aggregated totals keyed by [dev][month][status]
  const totals = {}
  developments.forEach(dev => {
    totals[dev] = {}
    sortedMonths.forEach(m => {
      totals[dev][m] = { raised: 0, to_raise: 0 }
    })
  })

  props.raises.forEach(item => {
    const month = item.month.substring(0, 7)
    const status = item.status === 'raised' ? 'raised' : 'to_raise'
    if (totals[item.development]?.[month]) {
      totals[item.development][month][status] += item.amount
    }
  })

  // Build series: one "Raised" + one "Still to Raise" per development
  const series = []
  const legendItems = []

  developments.forEach((dev, idx) => {
    const solidColour = DEV_COLOURS[idx % DEV_COLOURS.length]
    const lightColour = lightenHex(solidColour)

    legendItems.push({ name: `${dev} – Raised`, itemStyle: { color: solidColour } })
    legendItems.push({ name: `${dev} – Still to Raise`, itemStyle: { color: lightColour } })

    series.push({
      name: `${dev} – Raised`,
      type: 'bar',
      stack: `${dev}`,
      itemStyle: { color: solidColour },
      data: sortedMonths.map(m => totals[dev][m].raised || 0)
    })

    series.push({
      name: `${dev} – Still to Raise`,
      type: 'bar',
      stack: `${dev}`,
      itemStyle: {
        color: lightColour,
        borderColor: solidColour,
        borderWidth: 1,
        borderType: 'dashed'
      },
      data: sortedMonths.map(m => totals[dev][m].to_raise || 0)
    })
  })

  const option = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let result = `<strong>${params[0].axisValue}</strong><br/>`
        params.forEach(param => {
          if (param.value > 0) {
            result += `${param.marker}${param.seriesName}: R${param.value.toLocaleString()}<br/>`
          }
        })
        return result
      }
    },
    legend: {
      data: legendItems,
      bottom: 0,
      type: 'scroll',
      textStyle: { fontSize: 11 }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '20%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: sortedMonths.map(formatMonthLabel)
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => `R${(value / 1000000).toFixed(1)}M`
      }
    },
    series
  }

  chart.setOption(option, true)
}

watch(() => props.raises, () => {
  nextTick(() => { initChart() })
}, { deep: true })

onMounted(() => {
  nextTick(() => {
    if (chartContainer.value) {
      initChart()
    } else {
      setTimeout(() => { if (chartContainer.value) initChart() }, 100)
    }
  })
})
</script>
